// Task 7.5: Hero Section with Optimized Messaging

'use client';

import { useEffect, useMemo, useState } from 'react';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Check, FileText, Upload } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { CurrencyManager } from '@/lib/marketing/currency-manager';
import { formatPrice } from '@/lib/marketing/currency-utils';
import { isFoundingPartnerPhase } from '@/lib/feature-flags/launch-phase';

interface HeroSectionProps {
  variant: 'A' | 'B';
  onCtaClick: () => void;
  onTryWithData?: () => void;
}

export function HeroSection({ variant, onCtaClick, onTryWithData }: HeroSectionProps) {
  const founding = isFoundingPartnerPhase();
  const locale = useLocale();
  const currencyManager = useMemo(() => new CurrencyManager(), []);
  const [currencyCode, setCurrencyCode] = useState<string>('GBP');

  useEffect(() => {
    // Derive a stable currency for the hero price hint using locale.
    const resolved = (typeof locale === 'string' ? locale : 'en-GB').toLowerCase();
    if (resolved.startsWith('en-ie') || resolved.startsWith('de-de') || resolved.startsWith('de-at')) {
      setCurrencyCode('EUR');
    } else if (resolved.startsWith('de-ch')) {
      setCurrencyCode('CHF');
    } else {
      setCurrencyCode('GBP');
    }
  }, [locale]);

  const starterPrice = currencyManager.getPlanPrice('STARTER', currencyCode);
  const formattedStarter = formatPrice(starterPrice, currencyCode, locale);

  const handleSeePricing = () => {
    const checkoutUrl = process.env.NEXT_PUBLIC_STARTER_CHECKOUT_URL;
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      analytics.ctaClick('hero_see_pricing', variant, { channel: 'checkout', cta_label: 'See Pricing' });
    } else {
      const pricingSection = document.getElementById('pricing');
      pricingSection?.scrollIntoView({ behavior: 'smooth' });
      analytics.ctaClick('hero_see_pricing', variant, { channel: 'pricing-scroll', cta_label: 'See Pricing' });
    }
    onCtaClick();
  };

  const handleRequestAccess = () => {
    window.location.assign('/contact?subject=early-access');
    analytics.ctaClick('hero_request_access', variant, { channel: 'route', cta_label: 'Request Early Access' });
    onCtaClick();
  };

  const handleSeeDemoWorkspace = () => {
    window.location.assign('/demo-workspace');
    analytics.ctaClick('hero_demo_workspace', variant, { channel: 'route', cta_label: 'Open Demo Workspace' });
  };

  const handleTryWithData = () => {
    if (onTryWithData) {
      onTryWithData();
    } else {
      const importSection = document.getElementById('pre-auth-import');
      importSection?.scrollIntoView({ behavior: 'smooth' });
    }
    analytics.ctaClick('hero_try_with_data', variant, { channel: 'scroll', cta_label: 'Try with Your Data' });
  };

  return (
    <section className="relative pt-20 pb-24 lg:pt-32 lg:pb-32 overflow-hidden bg-background">
      {/* Hero Background Mesh - Pure Visual Upgrade */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl">
          <div className="absolute top-[10%] left-[20%] w-72 h-72 bg-primary/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-[20%] right-[20%] w-96 h-96 bg-blue-500/5 rounded-full blur-[120px]" />
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          
          {/* Badge - Stylized but Original Text */}
          <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <Badge
              variant="secondary"
              className="max-w-full whitespace-normal justify-center px-4 py-1.5 text-center text-sm leading-snug bg-primary/10 text-primary hover:bg-primary/15 border-primary/20 transition-colors"
            >
              Built for Quality Managers in AS9100 &amp; ISO 9001 manufacturing
            </Badge>
          </div>

          {/* Headline - Original Copy, Better Typography */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-8 text-foreground leading-[1.1] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
            When the auditor points at a tool,
            <br className="hidden md:block" />
            <span className="text-primary">how fast can you prove compliance?</span>
          </h1>

          {/* Subheadline - Original Copy */}
          <p className="text-xl md:text-2xl text-muted-foreground mb-10 leading-relaxed max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
            {founding
              ? '60-second evidence retrieval. Controlled OOT workflow with Blast Radius review for potentially affected use. Go live in 48 hours.'
              : '60-second evidence retrieval. Controlled OOT workflow with Blast Radius review for potentially affected use. Go live in 48 hours. 30-day money-back guarantee.'}
          </p>

          <p className="max-w-3xl mx-auto mb-8 text-sm leading-relaxed text-muted-foreground animate-in fade-in slide-in-from-bottom-8 duration-700 delay-250">
            AS9100D 7.1.5.2 · NADCAP · IATF 16949 · ISO 9001 · ISO 17025 · 21 CFR Part 11
          </p>

          {/* CTA Group - Visual Polish Only */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300">
            <Button
              size="lg"
              onClick={handleSeeDemoWorkspace}
              className="h-12 px-8 text-base font-bold shadow-lg shadow-primary/20 hover:shadow-primary/30 hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            >
              Open Demo Workspace
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={founding ? handleRequestAccess : handleSeePricing}
              className="h-12 px-8 text-base font-semibold border-input bg-background hover:bg-accent hover:text-accent-foreground transition-all duration-300 w-full sm:w-auto"
            >
              {founding ? 'Request Early Access' : 'See Pricing'}
            </Button>
          </div>

          {/* Try with Your Data CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-6 animate-in fade-in slide-in-from-bottom-12 duration-700 delay-350">
            <Button
              variant="ghost"
              onClick={handleTryWithData}
              className="w-full sm:w-auto text-muted-foreground hover:text-foreground gap-2"
            >
              <Upload className="w-4 h-4" />
              Try with your own data
            </Button>
            <Button
              asChild
              variant="ghost"
              className="w-full sm:w-auto h-auto min-h-10 whitespace-normal text-center text-muted-foreground hover:text-foreground gap-2"
            >
              <a href="/api/sample-pdf" target="_blank" rel="noopener noreferrer">
                <FileText className="w-4 h-4 shrink-0" />
                See a real evidence pack (PDF, no signup)
              </a>
            </Button>
          </div>

          {/* Sub-CTA text - Original Copy */}
          <div className="space-y-2 text-sm text-muted-foreground mb-10 animate-in fade-in slide-in-from-bottom-12 duration-700 delay-400">
            {founding ? (
              <p>Currently onboarding founding partner teams</p>
            ) : (
              <p>{formattedStarter}/mo • 30-day money-back guarantee</p>
            )}
            <p>Loads sample data • No signup</p>
          </div>

          {/* Trust Signals - Original Facts Only */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom-12 duration-700 delay-500">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/30 border border-border/50">
              <Shield className="w-4 h-4 text-primary" />
              <span>European data residency</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/30 border border-border/50">
              <Check className="w-4 h-4 text-green-500" />
              <span>TLS 1.3 Encryption</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/30 border border-border/50">
              <Check className="w-4 h-4 text-green-500" />
              <span>Automated backups</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/30 border border-border/50">
              <Shield className="w-4 h-4 text-primary" />
              <span>US region on request</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
